import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions, isSuperAdmin } from "@/lib/roles";
import { UserMenu } from "@/components/UserMenu";

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: ["admin-me"],
    queryFn: () => adminAuth.me(),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logout = useMutation({
    mutationFn: () => adminAuth.logout(),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["admin-me"] });
      navigate("/admin/login", { replace: true });
    },
  });

  if (me.isLoading) {
    return <div className="grid min-h-screen place-items-center text-brand-ink/60">Checking session…</div>;
  }
  if (me.isError || !me.data) {
    const back = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/admin/login?returnTo=${back}`} replace />;
  }

  const navItem = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-2.5 py-1.5 text-sm transition
     ${isActive
        ? "bg-brand-light font-semibold text-brand"
        : "text-brand-ink/80 hover:bg-brand-light hover:text-brand"}`;

  return (
    <div className="min-h-screen bg-brand-light">
      <header className="sticky top-0 z-20 border-b border-brand-ink/10 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2">
          <Link to="/admin" aria-label="Admin dashboard" className="shrink-0">
            <img
              src="/logo.png"
              alt="Baba Farid Group of Institutions"
              className="h-10 w-auto"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = "none";
                const fallback = document.createElement("span");
                fallback.className = "text-lg font-bold text-brand";
                fallback.textContent = "OTA Admin";
                img.parentElement?.appendChild(fallback);
              }}
            />
          </Link>

          <nav className="flex flex-1 flex-wrap items-center gap-1 overflow-x-auto">
            <NavLink to="/admin" end className={navItem}>Dashboard</NavLink>
            <NavLink to="/admin/quizzes" className={navItem}>Quizzes</NavLink>
            {canManageQuestions(me.data) && (
              <>
                <NavLink to="/admin/questions" className={navItem}>Questions</NavLink>
                <NavLink to="/admin/categories" className={navItem}>Categories</NavLink>
              </>
            )}
            <NavLink to="/admin/candidates" className={navItem}>Candidates</NavLink>
            <NavLink to="/admin/audit-log" className={navItem}>Audit log</NavLink>
            {canManageQuestions(me.data) && (
              <NavLink to="/admin/settings" className={navItem}>Settings</NavLink>
            )}
            {isSuperAdmin(me.data) && (
              <NavLink to="/admin/users" className={navItem}>Users</NavLink>
            )}
          </nav>

          <UserMenu me={me.data} onSignOut={() => logout.mutate()} signingOut={logout.isPending} />
        </div>
      </header>

      <Outlet />
    </div>
  );
}
